import {toast} from '@wandb/components';
import {type ExecutionResult} from 'graphql';
import {useCallback} from 'react';
import {useParams} from 'react-router';

import {
  type CreateNotebookMutation,
  type CreateSandboxInput,
  type CreateSandboxMutation,
  type DuplicateNotebookMutation,
  NotebookTemplate,
  SandboxMode,
  useCreateNotebookMutation,
  useCreateSandboxMutation,
  useDuplicateNotebookMutation,
} from '../../../../generated/graphql';
import {useOpenMarimoNotebookRuntime} from '../../../../routes/notebooks/useOpenMarimoNotebookRuntime';
import {
  recordRumAction,
  recordRumError,
} from '../../../../util/datadog/datadogRum';
import {propagateErrorsContext} from '../../../../util/errors';
import {showNotebookQuotaModalForError} from '../../../NotebookQuotaModal/notebookQuotaModalStore';
import {useStablePanelId} from '../../../Panel/PanelIDContext';
import {type MarimoConfig} from '../../../PanelMarimo/common';
import {MarimoMode, useMarimoState} from '../../../PanelMarimo/store';
import {useMarimoSettingsStateContext} from './MarimoSettingsContextProvider';

export const LOADING_STRINGS = [
  'Loading notebook...',
  'Creating sandbox...',
  'Setting up environment...',
  'Installing packages...',
  'Connecting to API...',
];

const LOADING_STRING_INTERVAL = 3000;

async function forkOrCreateNotebook(
  createOrForkMutation: () => Promise<
    ExecutionResult<CreateNotebookMutation | DuplicateNotebookMutation>
  >,
  updateConfig: (config: MarimoConfig) => void,
  onNotebookCreation: (value: string | null) => void
) {
  const errorMsg = 'Error creating notebook';
  try {
    const notebookResult = await createOrForkMutation();
    let notebookId: string | undefined;
    if (notebookResult.data && 'createNotebook' in notebookResult.data) {
      notebookId = notebookResult.data?.createNotebook?.notebook?.id;
    } else if (
      notebookResult.data &&
      'duplicateNotebook' in notebookResult.data
    ) {
      notebookId = notebookResult.data?.duplicateNotebook?.notebook?.id;
    }

    if ((notebookResult.errors?.length ?? 0) > 0) {
      handleError(notebookResult.errors![0]!, errorMsg);
      return undefined;
    }

    if (notebookId) {
      updateConfig({
        notebookId,
      });
      onNotebookCreation(notebookId);
      return notebookId;
    } else {
      handleError(new Error('No notebook ID returned'), errorMsg);
      return undefined;
    }
  } catch (error) {
    handleError(error as Error, errorMsg);
    return undefined;
  }
}

function handleError(error: Error, errorMsg: string) {
  // Out of storage or sandbox credit: the quota modal is the answer, not a
  // failure toast. The sandbox message marks a launch; the rest are creates.
  const action = errorMsg.includes('sandbox') ? 'launch' : 'create';
  if (showNotebookQuotaModalForError(error, action)) {
    return;
  }
  toast.error(errorMsg);
  recordRumError(new Error(errorMsg), {
    context: {
      message: error.message,
    },
  });
}

async function createSandbox(
  createSandboxMutation: ({
    variables,
  }: {
    variables: CreateSandboxInput;
  }) => Promise<ExecutionResult<CreateSandboxMutation>>,
  notebookId: string,
  entityName: string,
  projectName: string,
  runNames: string[],
  mode: SandboxMode
): Promise<{url: string; sandboxId: string; token: string} | undefined> {
  const errorMsg = 'Error creating sandbox';
  try {
    const sandboxResult = await createSandboxMutation({
      variables: {
        notebookId,
        mode,
        entityName,
        projectName,
        runNames,
      },
    });
    if ((sandboxResult.errors?.length ?? 0) > 0) {
      handleError(sandboxResult.errors![0]!, errorMsg);
      return undefined;
    }
    const url = sandboxResult.data?.createSandbox?.url;
    const sandboxId = sandboxResult.data?.createSandbox?.id;
    const token = sandboxResult.data?.createSandbox?.token;
    if (!url) {
      handleError(new Error('No sandbox URL returned'), errorMsg);
      return undefined;
    }
    if (!sandboxId) {
      handleError(new Error('No sandbox ID returned'), errorMsg);
      return undefined;
    }
    if (!token) {
      handleError(new Error('No sandbox token returned'), errorMsg);
      return undefined;
    }
    return {url, sandboxId, token};
  } catch (error) {
    handleError(error as Error, errorMsg);
    return undefined;
  }
}

function startLoading(
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  setIsLoading(true);
  let i = 0;
  const id = window.setInterval(() => {
    i = (i + 1) % LOADING_STRINGS.length;
    setLoadingString(LOADING_STRINGS[i]);
  }, LOADING_STRING_INTERVAL);
  return () => window.clearInterval(id);
}

function useForkOrCreateNotebookClick(
  createOrForkMutation: () => Promise<
    ExecutionResult<CreateNotebookMutation | DuplicateNotebookMutation>
  >,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookCreation: (value: string | null) => void
) {
  const {updateConfig} = useMarimoSettingsStateContext();
  return useCallback(async () => {
    setIsLoading(true);
    setLoadingString('Creating notebook...');
    try {
      return await forkOrCreateNotebook(
        createOrForkMutation,
        updateConfig,
        onNotebookCreation
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    createOrForkMutation,
    updateConfig,
    setIsLoading,
    setLoadingString,
    onNotebookCreation,
  ]);
}

export const useOnCreateNotebookClick = (
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookCreation: (value: string | null) => void
) => {
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  const [createNotebookMutation] = useCreateNotebookMutation({
    variables: {template: NotebookTemplate.Basic, entityName, projectName},
    context: propagateErrorsContext(),
  });
  const createNotebook = useForkOrCreateNotebookClick(
    createNotebookMutation,
    setIsLoading,
    setLoadingString,
    onNotebookCreation
  );
  const openNotebookRuntime = useOpenMarimoNotebookRuntime();
  return useCallback(async () => {
    const notebookId = await createNotebook();
    if (notebookId == null) {
      return;
    }

    openNotebookRuntime(notebookId, undefined, {newTab: true});
  }, [createNotebook, openNotebookRuntime]);
};

export function useOnForkNotebookClick(
  selectedOptionID: string | null,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void,
  onNotebookFork: (value: string | null) => void
) {
  const [duplicateNotebookMutation] = useDuplicateNotebookMutation({
    variables: {id: selectedOptionID ?? ''},
    context: propagateErrorsContext(),
  });
  const forkNotebook = useForkOrCreateNotebookClick(
    duplicateNotebookMutation,
    setIsLoading,
    setLoadingString,
    onNotebookFork
  );
  const openNotebookRuntime = useOpenMarimoNotebookRuntime();
  return useCallback(async () => {
    const notebookId = await forkNotebook();
    if (notebookId == null) {
      return;
    }

    openNotebookRuntime(notebookId, undefined, {newTab: true});
  }, [forkNotebook, openNotebookRuntime]);
}

function useCreateSandboxAction(
  notebookId: string,
  mode: SandboxMode,
  onUrl: (url: string, sandboxId: string, token: string) => void,
  setIsLoading: (b: boolean) => void,
  setLoadingString: (s: string) => void,
  runNames: string[]
) {
  const [createSandboxMutation] = useCreateSandboxMutation({
    context: {...propagateErrorsContext(), doNotRetry: true},
  });
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();
  return useCallback(async () => {
    const stopLoading = startLoading(setIsLoading, setLoadingString);
    try {
      const sandboxResult = await createSandbox(
        createSandboxMutation,
        notebookId,
        entityName,
        projectName,
        runNames,
        mode
      );
      if (sandboxResult == null) {
        return;
      }
      const {url, sandboxId, token} = sandboxResult;
      onUrl(url, sandboxId, token);
    } finally {
      stopLoading();
      setIsLoading(false);
    }
  }, [
    createSandboxMutation,
    notebookId,
    entityName,
    projectName,
    setIsLoading,
    setLoadingString,
    onUrl,
    mode,
    runNames,
  ]);
}

function useOnClickRunCodeButton(
  notebookId: string,
  panelId: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const setPanelState = useMarimoState(state => state.setPanelState);
  const runNames = useMarimoState(
    state => state.getPanelState(panelId).runNames
  );
  const onUrl = useCallback(
    (url: string, sandboxId: string, token: string) => {
      setPanelState(panelId, {
        mode: MarimoMode.RUN,
        url,
        sandboxId,
        token,
      });
      recordRumAction('panel_marimo_notebook_run_sandbox_created', {
        notebookId,
        panelId,
        sandboxId,
      });
    },
    [setPanelState, panelId, notebookId]
  );
  return useCreateSandboxAction(
    notebookId,
    SandboxMode.Run,
    onUrl,
    setIsLoading,
    setLoadingString,
    runNames
  );
}

export function useOnClickRunCodeButtonInSettings(
  notebookId: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const {panelId} = useMarimoSettingsStateContext();
  return useOnClickRunCodeButton(
    notebookId,
    panelId,
    setIsLoading,
    setLoadingString
  );
}

export function useOnClickRunCodeButtonInPanel(
  notebookId: string,
  setIsLoading: (isLoading: boolean) => void,
  setLoadingString: (loadingString: string) => void
) {
  const panelId = useStablePanelId();
  return useOnClickRunCodeButton(
    notebookId,
    panelId,
    setIsLoading,
    setLoadingString
  );
}

function useOnClickViewNotebookButton(notebookId: string) {
  const openNotebookRuntime = useOpenMarimoNotebookRuntime();
  return useCallback(
    () => openNotebookRuntime(notebookId, undefined, {newTab: true}),
    [notebookId, openNotebookRuntime]
  );
}

export function useOnClickViewNotebookButtonInSettings(notebookId: string) {
  return useOnClickViewNotebookButton(notebookId);
}
